import { MontantCalculDirective } from '../../../directives/montant-calcul.directive';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { OperationPlanifiee } from '../../../models/operation-planifiee.model';
import { Compte } from '../../../models/compte.model';
import { Categorie } from '../../../models/categorie.model';
import { PopupHistoriquePlanifieeComponent } from '../popup-historique-planifiee/popup-historique-planifiee.component';

export interface EditionPlanifieePayload {
  libelle: string;
  type: string;
  montant: number;
  dateDebut: string;
  jourDuMois: number;
  nombreDeMois: number | null;
  estEtalee: boolean;
  nombreMensualites: number | null;
  montantInitial: number | null;
  provision: boolean;
  valeurRestante: number | null;
  moyenPaiement: string | null;
  numeroCheque: string | null;
  compteDestinationId: number | null;
  compteDestinationExterne: string | null;
  virementProgramme: boolean;
  categorie: string;
  periodicite: number;
}

@Component({
    selector: 'app-popup-edition-planifiee',
    imports: [ReactiveFormsModule, MontantCalculDirective, PopupHistoriquePlanifieeComponent],
    templateUrl: './popup-edition-planifiee.component.html',
    styleUrl: './popup-edition-planifiee.component.scss'
})
export class PopupEditionPlanifieeComponent implements OnChanges {
  /** null = création, objet = édition. null + visible=false = caché */
  @Input() operation: OperationPlanifiee | null = null;
  @Input() visible = false;
  @Input() erreur: string | null = null;
  @Input() categories: Categorie[] = [];
  @Input() moyensPaiement: readonly { label: string; value: string }[] = [];
  @Input() comptesDisponibles: Compte[] = [];
  @Input() groupeIds: number[] | null = null;

  @Output() sauvegarder = new EventEmitter<EditionPlanifieePayload>();
  @Output() annuler = new EventEmitter<void>();
  @Output() supprimer = new EventEmitter<number>();
  @Output() supprimerGroupe = new EventEmitter<number[]>();
  @Output() ajouterCategorie = new EventEmitter<void>();

  @ViewChild('libelleInput') libelleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('montantInput') montantInput?: ElementRef<HTMLInputElement>;
  @ViewChild('historiqueBouton') historiqueBouton?: ElementRef<HTMLButtonElement>;

  historiqueVisible = false;

  confirmerSuppr = false;

  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group({
    libelle: ['', Validators.required],
    type: ['SORTIE', Validators.required],
    montant: [0 as number, Validators.required],
    dateDebut: ['', Validators.required],
    nombreDeMois: [null as number | null],
    jourDuMois: [1, [Validators.required, Validators.min(1), Validators.max(31)]],
    estEtalee: [false],
    moyenPaiement: [null as string | null],
    numeroCheque: [''],
    compteDestinationId: [null as number | null],
    compteDestinationExterne: [''],
    virementProgramme: [false],
    typeDestination: ['interne' as 'interne' | 'externe'],
    provision: [false],
    valeurRestante: [0 as number, [Validators.min(0)]],
    nombreMensualites: [null as number | null],
    montantInitial: [0 as number | null, [Validators.min(0.01)]],
    categorie: [''],
    periodicite: [1 as number, Validators.required],
  });

  get estCreation(): boolean {
    return this.visible && this.operation === null;
  }

  get etalementActif(): boolean {
    return !!this.form.get('estEtalee')?.value;
  }

  constructor() {
    this.form.controls.moyenPaiement.valueChanges.subscribe((moyen) => {
      const numeroCheque = this.form.controls.numeroCheque;
      numeroCheque.setValidators(moyen === 'CHEQUE'
        ? [Validators.required, Validators.pattern(/\S/)]
        : []);
      numeroCheque.updateValueAndValidity({ emitEvent: false });
      if (moyen !== 'VIREMENT') {
        this.form.controls.virementProgramme.setValue(false);
      }
    });

    this.form.get('provision')?.valueChanges.subscribe((active) => {
      const valeurCtrl = this.form.get('valeurRestante');
      if (!valeurCtrl) return;
      if (active) {
        if (!this.operation?.provision) {
          const montant = Number(this.form.get('montant')?.value ?? 0);
          valeurCtrl.setValue(Number.isFinite(montant) ? montant : 0);
        }
      } else {
        valeurCtrl.setValue(0);
      }
    });

    this.form.get('montant')?.valueChanges.subscribe((montant) => {
      const provisionActive = this.form.get('provision')?.value;
      const valeurCtrl = this.form.get('valeurRestante');
      if (!valeurCtrl || !provisionActive) return;
      if (!this.operation?.provision) {
        valeurCtrl.setValue(Number.isFinite(Number(montant)) ? Number(montant) : 0, { emitEvent: false });
      }
    });

    this.form.get('estEtalee')?.valueChanges.subscribe((actif) => {
      const montantInitialCtrl = this.form.get('montantInitial');
      const nombreMensualitesCtrl = this.form.get('nombreMensualites');
      if (actif) {
        montantInitialCtrl?.enable();
        nombreMensualitesCtrl?.enable();
      } else {
        montantInitialCtrl?.disable();
        nombreMensualitesCtrl?.disable();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['operation']) {
      this.confirmerSuppr = false;
      this.historiqueVisible = false;
      if (!this.visible) return;

      if (this.operation) {
        const op = this.operation;
        this.form.reset({
          libelle: op.libelle,
          type: op.type,
          montant: op.montant,
          dateDebut: op.dateDebut ?? '',
          nombreDeMois: op.nombreDeMois ?? null,
          jourDuMois: op.jourDuMois,
          estEtalee: op.estEtalee,
          provision: op.provision,
          valeurRestante: op.provision ? (op.valeurRestante ?? op.montant) : 0,
          moyenPaiement: op.moyenPaiement ?? null,
          numeroCheque: op.numeroCheque ?? '',
          compteDestinationId: op.compteDestinationId ?? null,
          compteDestinationExterne: op.compteDestinationExterne ?? '',
          virementProgramme: op.moyenPaiement === 'VIREMENT' && (op.virementProgramme ?? false),
          typeDestination: op.compteDestinationExterne ? 'externe' : 'interne',
          nombreMensualites: op.nombreMensualites ?? null,
          montantInitial: op.montantInitial ?? op.montant,
          categorie: op.categorie ?? '',
          periodicite: op.periodicite ?? 1,
        });
        if (op.estEtalee) {
          this.form.get('montantInitial')?.enable();
          this.form.get('nombreMensualites')?.enable();
        } else {
          this.form.get('montantInitial')?.disable();
          this.form.get('nombreMensualites')?.disable();
        }
        this.form.markAsPristine();
        setTimeout(() => {
          this.montantInput?.nativeElement.focus();
          this.montantInput?.nativeElement.select();
        }, 0);
      } else {
        const today = new Date().toISOString().slice(0, 10);
        this.form.reset({
          libelle: '',
          type: 'SORTIE',
          montant: 0,
          dateDebut: today,
          nombreDeMois: null,
          jourDuMois: 1,
          estEtalee: false,
          provision: false,
          valeurRestante: 0,
          moyenPaiement: null,
          numeroCheque: '',
          compteDestinationId: null,
          compteDestinationExterne: '',
          virementProgramme: false,
          typeDestination: 'interne',
          nombreMensualites: null,
          montantInitial: 0,
          categorie: '',
          periodicite: 1,
        });
        this.form.get('montantInitial')?.disable();
        this.form.get('nombreMensualites')?.disable();
        this.form.markAsPristine();
        setTimeout(() => {
          this.libelleInput?.nativeElement.focus();
        }, 0);
      }
    }
  }

  ouvrirHistorique(): void {
    if (this.operation) this.historiqueVisible = true;
  }

  fermerHistorique(): void {
    this.historiqueVisible = false;
    setTimeout(() => this.historiqueBouton?.nativeElement.focus(), 0);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const moyen = v.moyenPaiement ?? null;
    const estEtalee = !!v.estEtalee;
    const provision = !!v.provision;
    this.sauvegarder.emit({
      libelle: v.libelle ?? '',
      type: v.type ?? 'SORTIE',
      montant: Number(v.montant ?? 0),
      dateDebut: v.dateDebut ?? '',
      jourDuMois: Number(v.jourDuMois ?? 1),
      nombreDeMois: v.nombreDeMois ?? null,
      estEtalee,
      nombreMensualites: estEtalee ? (v.nombreMensualites ?? null) : null,
      montantInitial: estEtalee ? (v.montantInitial ?? null) : null,
      provision,
      valeurRestante: provision ? (v.valeurRestante ?? null) : null,
      moyenPaiement: moyen,
      virementProgramme: moyen === 'VIREMENT' && !!v.virementProgramme,
      numeroCheque: moyen === 'CHEQUE' ? (v.numeroCheque?.trim() || null) : null,
      compteDestinationId:
        moyen === 'VIREMENT' && v.typeDestination === 'interne' ? (v.compteDestinationId ?? null) : null,
      compteDestinationExterne:
        moyen === 'VIREMENT' && v.typeDestination === 'externe' ? (v.compteDestinationExterne?.trim() || null) : null,
      categorie: v.categorie ?? '',
      periodicite: Number(v.periodicite ?? 1),
    });
  }

  demanderSuppr(): void {
    this.confirmerSuppr = true;
  }

  annulerSuppr(): void {
    this.confirmerSuppr = false;
  }

  confirmerSupprDef(): void {
    if (this.operation) this.supprimer.emit(this.operation.id);
  }

  confirmerSupprGroupe(): void {
    if (this.groupeIds && this.groupeIds.length > 0) {
      this.supprimerGroupe.emit(this.groupeIds);
    } else if (this.operation?.groupeRef) {
      // groupeRef présent mais pas de groupeIds : le parent gérera
      this.supprimerGroupe.emit([]);
    }
  }
}
